import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';

export default Vue.component('video-layer', {
  template: require('./template.html'),
  name: 'video-layer',
  mixins: [guid, uploader],
  components: {
    uploadProgress,
  },
  computed: {
    ...mapGetters(['viewport', 'activeDroplet']),
    sectionRule() {
      return _.find(this.activeDroplet.mediaQueries, { selector: ' ' });
    },
  },
  props: ['option', 'active-state', 'dom-nodes'],
  data() {
    return {
      showConfirm: false,
      showFiles: false,
      loading: false,
      imageFiles: [],
      hoverDel: false,
      activeImg: null,
      activeIndex: null,
      display: {
        hasBgImg: false,
        bgImg: '',
      },
      debTime: 250,
      innerDomNodes: {},
      prefix: 'drz_section_',
      bucketPath: '_droplet-assets/section/',
      thumbnails: '_droplet-assets/_thumbnails/section/',
      cacheBgImg: '',
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
  },
  created() {
    // then check for previous values
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      const payload = this.option.payload;
      this.display.bgImg = this.getBgImg(payload);
    },
    getBgImg() {
      let bg = 'unset';
      let desktop;
      if (this.sectionRule) {
        const val = _.find(this.sectionRule.properties, { property: 'background-image' });
        if (val) {
          desktop = val.desktop;
          bg = val[this.viewport];
        }
      }

      const hasImg = (bg !== 'unset' && bg !== 'default') || (bg === 'default' && desktop !== 'default' && desktop !== 'unset');
      this.display.hasBgImg = hasImg;
      if (!hasImg) {
        bg = '';
      }
      if (bg === 'default' && desktop !== 'default' && desktop !== 'unset') {
        bg = desktop;
      }
      return bg;
    },
    getFileName() {
      let name = '';
      if (_.isString(this.display.bgImg)) {
        const tail = this.display.bgImg.split('/').pop();
        name = tail.replace('\')', '');
        name = name.replace(')', '');
      }
      if (name === 'default') {
        const val = _.find(this.sectionRule.properties, { property: 'background-image' });
        name = val.desktop.split('/').pop();
        name = name.replace('\')', '');
        name = name.replace(')', '');
      }
      return name;
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    async uploadBg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          let newFile = [...e.target.files];
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `${this.prefix}${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');
            try {
              // send off to our s3 upload mixin
              const filePath = `/${this.bucketPath}${fileName}`;
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: newFile,
                thumbnail: {
                  width: 35,
                  srcFile: newFile,
                  name: fileName,
                  bucketPath: `${this._sitePath}/${this.thumbnails}`,
                },
              });
              await this.setSectionVideo(filePath);
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'section uploadBg',
                });
              }
            } finally {
              if (this.showFiles) {
                setTimeout(() => {
                  this.fetchImageFiles();
                }, 500);
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async setSectionVideo(f, reset, vp) {
      this.cacheBgImg = this.activeDroplet.bgImg;
      // if came from click, gather full url
      let file = f;
      let isVideo = false;

      if (f) {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
        const extension = (path.extname(file)).toLowerCase();
        isVideo = ['.mov', '.mp4'].includes(extension);
      }

      const payload = {};
      payload.drp = this.activeDroplet;
      payload.val = reset ? 'default' : `url('${file}')`;
      payload.selector = ' ';
      payload.property = 'background-image';
      payload.viewport = vp || this.viewport;
      this.$store.dispatch('changeDropletCss', payload);
      this.$emit('check-legacy-styles', payload);
      this.$emit('css-generator');
      this.updateVideo(isVideo, file, payload.viewport);

      // reset local values
      this.display.hasBgImg = true;
      this.display.bgImg = payload.val;
      this.$emit('update-changes', true);
    },
    async updateVideo(videoBg, url, vp) {
      const p = _.cloneDeep(this.activeDroplet);
      const defaults = {
        desktop: { src: false, type: false, inherit: true },
        tablet: { src: false, type: false, inherit: true },
        mobile: { src: false, type: false, inherit: true },
      };
      let unset = false;
      if (!p.videoBg) {
        p.videoBg = defaults;
        unset = true;
      }
      if (videoBg && videoBg !== 'none') {
        let type = path.extname(url);
        if (type === '.mov') {
          type = '.mp4';
        }
        p.videoBg[vp].src = url;
        p.videoBg[vp].type = type.replace('.', '');
        // check if tablet & mobile need to inherit from desktop
        if (vp === 'desktop') {
          const bg = _.find(this.sectionRule.properties, {
            property: 'background-image',
          });
          if (bg && p.videoBg.mobile.inherit) {
            if (unset && bg.mobile !== 'default') {
              p.videoBg.mobile.inherit = false;
            } else {
              p.videoBg.mobile.src = url;
              p.videoBg.mobile.type = type.replace('.', '');
            }
          }
          if (bg && p.videoBg.tablet.inherit) {
            if (unset && bg.tablet !== 'default') {
              p.videoBg.tablet.inherit = false;
            } else {
              p.videoBg.tablet.src = url;
              p.videoBg.tablet.type = type.replace('.', '');
            }
          }
        }
      }
      if (!videoBg || videoBg === 'none') {
        // update photo bgs to include no video
        // or if removing them
        p.videoBg[vp].src = false;
        p.videoBg[vp].type = false;
        if (vp === 'desktop') {
          if (p.videoBg.mobile.inherit) {
            p.videoBg.mobile.src = false;
            p.videoBg.mobile.type = false;
          }
          if (p.videoBg.tablet.inherit) {
            p.videoBg.tablet.src = false;
            p.videoBg.tablet.type = false;
          }
        }
      }
      // when any new section bg gets changed, inherit will always be false
      if (videoBg !== 'none') {
        p.videoBg[vp].inherit = false;
      }
      // make sure there is at least 1 true
      const videoList = _.map(p.videoBg, vp => vp.src);
      let hasVideo = false;
      for (let i = 0; i < videoList.length; i++) {
        if (typeof videoList[i] === 'string') {
          hasVideo = true;
          break;
        }
      }
      this.$store.dispatch('setDropletVal', {
        drp: this.activeDroplet,
        key: 'hasVideo',
        val: hasVideo,
      });

      this.$store.dispatch('setDropletVal', {
        drp: this.activeDroplet,
        key: 'videoBg',
        val: p.videoBg || defaults,
      });
      this.$emit('css-generator', false, this.option.plugin);
    },
    removeBgImg() {
      const payload = {};
      payload.drp = this.activeDroplet;
      payload.val = 'default';
      payload.selector = ' ';
      payload.property = 'background-image';
      payload.viewport = this.viewport;
      this.$store.dispatch('changeDropletCss', payload);
      this.$emit('check-legacy-styles', payload);
      this.$emit('css-generator', false, this.option.plugin);
      this.updateVideo('none', false, payload.viewport);
      this.$emit('update-changes', true);
      // reset local values
      this.display.hasBgImg = false;
      this.display.bgImg = 'unset';
    },
    async deleteImg() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this._getFromThumb(this.activeImg));
      const thumb = path.basename(this.activeImg);
      try {
        this.loading = true;
        // check session first
        await this._cognitoCheckSession();
        // delete original from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        });
        // delete thumbnail from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted image: ${file}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteImg',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.loading = false;
        this.showConfirm = false;
        this.fetchImageFiles();
      }

      // if deleting image that is being used, reset any viewports using it to default
      const deleted = `/${this.bucketPath}${file}`;
      const mq = _.find(this.sectionRule.properties, { property: 'background-image' });
      if (mq) {
        ['desktop', 'tablet', 'mobile'].forEach((vp) => {
          let val = mq[vp].replace('url(\'', '');
          val = val.replace('\')', '');
          if (val === deleted) {
            this.setSectionVideo(false, true, vp);
          }
        });
      }
      this.$emit('check-legacy-styles', { selector: ' ', property: 'background-image' });
    },
    async fetchImageFiles() {
      try {
        this.loading = true;
        const assetsFolder = '/_droplet-assets';
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.thumbnails}`);
        const videosOnly = files.filter(f => ['.mov', '.mp4'].includes(path.extname(this._getFromThumb(f))));
        this.imageFiles = videosOnly.map(f => `${assetsFolder}${f.split(assetsFolder)[1]}`);
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'fetchImageFiles',
        });
      } finally {
        this.loading = false;
      }
    },
    confirmDelete(file) {
      this.activeImg = file;
      this.toggleConfirm();
    },
    toggleConfirm() {
      this.showConfirm = !this.showConfirm;
    },
    toggleFileBrowser() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchImageFiles();
      }
    },
    matchBg(image) {
      let display = this.display.bgImg.replace('url(', '');
      display = display.replace('\')', '');
      display = display.split('/').pop();
      const img = this._getFromThumb(path.basename(image));
      return img === display;
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/section-options/video-layer/index.js